/**
 * Otto — the old boar in the fattening hall.
 *
 * ## Two attempts failed here, and they failed the same way
 *
 * Session 16 built him twice. Both passed typecheck, lint, the triangle budget
 * and the origin contract, and both were unusable. The first was a saddle-backed
 * dachshund; the second fixed the saddle and became **a loaf** — one continuous
 * blob from rump to nose with the head merged into the torso and no wedge.
 *
 * The diagnosis, and the whole reason this file is shaped the way it is: **both
 * attempts put the head at the same height as the back.** That is the loaf. A
 * boar's outline is not a tube with features on it; it is *two peaks and a
 * valley* —
 *
 *   1. the topline **rises** from a wasted rump to a crest over the shoulder,
 *      which is the highest point on the animal and sits well forward;
 *   2. it then **drops hard**, 12 cm in 15 cm of length, into the neck;
 *   3. the head hangs **below** that crest and slopes down again to the nose.
 *
 * Get those three and the animal reads at 64 px with no detail on it at all.
 * Miss them and no amount of stitching, tusking or bald-patching will save it —
 * which is exactly what the second attempt proved by having all three and still
 * being rejected.
 *
 * ## What carries the read, in order
 *
 * **Mass in front.** He is widest (0.41 half-width), tallest (the crest) and
 * heaviest at the shoulder, and everything behind it falls away. The hindquarters
 * are *narrow*; that contrast is the wedge, and the wedge is the character.
 *
 * **Tusks that cross the outline.** The brief's most emphatic line, failed twice.
 * A tusk beside the jaw is invisible at 64 px however long it is. These leave the
 * lower jaw and sweep up, out and back until the tip is *above* the muzzle's
 * topline and *outside* the head's width — so they break the outline in the side
 * view and in the front view both. That is the only property that matters and
 * every number in `TUSK` is chosen to satisfy it.
 *
 * **Asymmetry.** One ear is his; the other was sewn on from some other toy
 * entirely, and it is a different shape, a different size and a different angle
 * as well as a different fabric. A symmetrical head is a much weaker silhouette
 * than an unbalanced one, and here the asymmetry is also the story.
 *
 * ## He is a plush, and the repairs are the point
 *
 * Everything in this hall is a soft toy. Otto is an old one that somebody kept
 * mending: bald patches worn through to the backing, coarse mismatched thread,
 * a replacement ear. **The repairs are generated on the torso's own parametric
 * surface** (`torsoPoint`) and pushed out along its normal, which is why they can
 * never float or clip the way the previous pass's free-standing ellipsoid shells
 * did. A repair that sits *on* him says he was mended; a dark blob hovering near
 * him says the mesh is broken.
 *
 * ## Scale
 *
 * `OTTO_SCALE` is measured from the geometry, not declared at it (D-074), and
 * `build()` asserts the mesh against it on every construction. He is 3.3× Rosalie's
 * height: the pen must visibly be too small for him, and he must visibly be too
 * big for the slat and the vent.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { OTTO } from '@/render/palette';
import { surface } from './materials';
import type { AssetDefinition } from './types';

/**
 * His standing dimensions in metres, **measured** from the built mesh.
 *
 * D-074: `footprint` is what the geometry measures, not what the author meant.
 * `build()` re-measures and throws if these drift, so a later proportion change
 * cannot quietly leave the hall placing him against numbers that stopped being
 * true.
 */
export const OTTO_SCALE = { length: 2.35, width: 0.82, height: 1.06 } as const;

/** How far the declared scale may miss the measured bounds before it is a bug. */
const SCALE_TOLERANCE = 0.02;

/**
 * The long-axis roll pivot, at the barrel's centre of mass.
 *
 * Beat 3 stage 4 rolls him onto his back and back down, and the whole point of
 * the shot is that it is *heavy* — so the pivot has to be where his weight is,
 * not at his feet. A roll about the floor line looks like a door opening.
 */
const ROLL_Y = 0.6;

/** Neck pivot. Buried under the shoulder shield, not perched on its surface. */
const NECK = { y: 0.68, z: 0.52 } as const;

// ── The topline, as sampled numbers ───────────────────────────────────────────
//
// These are authored measurements, not a decorative spline, and the peak/valley
// relationship between them is the asset. Read the `top` column downward: 0.755
// at the rump, 1.018 at the shoulder, 0.978 immediately in front of it. That
// 40 mm fall in 95 mm is the break the head hangs off, and it is the number this
// file exists to protect.
//
// `bottom` is not a mirror of `top`. An old boar's belly sags lowest at mid-body
// while his back is settling there too, so the barrel is *deepest* behind the
// shoulder and shallowest at both ends.
const TORSO = [
  { z: -1.175, top: 0.655, bottom: 0.5, halfWidth: 0.105 },
  { z: -1.03, top: 0.745, bottom: 0.4, halfWidth: 0.195 },
  { z: -0.82, top: 0.822, bottom: 0.315, halfWidth: 0.272 },
  { z: -0.45, top: 0.878, bottom: 0.272, halfWidth: 0.306 },
  { z: -0.1, top: 0.932, bottom: 0.268, halfWidth: 0.338 },
  { z: 0.15, top: 0.998, bottom: 0.3, halfWidth: 0.382 },
  { z: 0.31, top: 1.022, bottom: 0.352, halfWidth: 0.41 },
  { z: 0.45, top: 0.985, bottom: 0.398, halfWidth: 0.35 },
  { z: 0.55, top: 0.94, bottom: 0.44, halfWidth: 0.26 },
] as const;

/**
 * The skull, in coordinates local to the neck pivot, and it only ever descends.
 *
 * `cy` is the centre-line of the head and it falls monotonically from 0.04 to
 * −0.262 — the head is *carried down*, which is what a heavy animal does and
 * what neither previous attempt did. Its highest point (0.935 in world Y) is
 * 12 cm below the crest, and that gap is the separation the loaf was missing.
 *
 * The widest and deepest section is the second one, not the first: that is the
 * jowl, and on an old boar it hangs below the line of the skull rather than
 * continuing it.
 */
const SKULL = [
  { z: -0.1, cy: 0.055, halfHeight: 0.155, halfWidth: 0.178 },
  { z: 0.05, cy: 0.015, halfHeight: 0.19, halfWidth: 0.195 },
  { z: 0.24, cy: -0.09, halfHeight: 0.158, halfWidth: 0.145 },
  { z: 0.42, cy: -0.205, halfHeight: 0.122, halfWidth: 0.11 },
  { z: 0.53, cy: -0.278, halfHeight: 0.097, halfWidth: 0.093 },
  { z: 0.615, cy: -0.315, halfHeight: 0.079, halfWidth: 0.081 },
] as const;

/**
 * Where the skull loft ends, local to the neck pivot.
 *
 * The snout disc and the nostrils are set *back* from it rather than on it,
 * because they are the frontmost geometry on him and his overall length is load
 * bearing: `docs/beat-3-plan.md` puts him in a 2.4 m pen so that he barely fits.
 * The first build of this file put the disc at `NOSE_Z` and the nostrils proud of
 * that, which measured 2.39 m — inside the pen by 5 mm a side, which does not
 * read as "barely fits", it reads as clipping. The scale assertion in `build()`
 * caught it.
 */
const NOSE_Z = 0.615;
const DISC_Z = NOSE_Z + 0.026;

/**
 * The tusks, given as the three points of the curve rather than as an angle.
 *
 * Stating the *tip* is the whole trick. An angle-and-length tusk is a tusk you
 * have to render to find out about; a tip at x = ±0.235, y = 0.79 is a tusk
 * whose position relative to the head — outside its 0.125 half-width there, and
 * 40 mm above its 0.75 topline — can be checked by reading the numbers. Both
 * previous attempts specified an angle and both were invisible at 64 px.
 *
 * World coordinates; `build()` converts them onto the head pivot.
 */
const TUSK = {
  root: { x: 0.085, y: 0.345, z: 1.02 },
  /** Pushed forward and out first, so it curves like a tooth instead of a spike. */
  control: { x: 0.15, y: 0.405, z: 1.055 },
  tip: { x: 0.215, y: 0.71, z: 0.92 },
  rootRadius: 0.042,
  tipRadius: 0.012,
} as const;

// Segment counts, and they are a budget decision as much as a shape one. The
// fattening hall is the tightest room in the game (D-052's gate leaves it a few
// hundred triangles), and the first build of this file spent its entire headroom
// on smoothness nobody can see at the distance the beat frames him from. Every
// count below was lowered until the turnaround showed a facet, then raised once.
const SEG = { torsoRings: 20, torsoRadial: 20, skullRings: 12, skullRadial: 14, limb: 8 } as const;

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * The bristle crest along his spine, as a height added to the topline.
 *
 * A boar's mane is the fastest "not a domestic pig" signal there is, and on a
 * plush it is a strip of longer pile rather than hair. It is added *into the
 * loft* — never as separate geometry — because a row of spines stuck along the
 * back reads as a stegosaurus, and any separate piece would leave the crease
 * that Rosalie's G1 rebuild removed by making her torso one surface.
 */
function crestAt(z: number): number {
  return 0.055 * smoothstep(-1.0, 0.28, z) * (1 - 0.8 * smoothstep(0.31, 0.5, z));
}

interface Section {
  readonly top: number;
  readonly bottom: number;
  readonly halfWidth: number;
}

/** Interpolate the authored table at an arbitrary z, easing between samples. */
function torsoSection(z: number): Section {
  const first = TORSO[0];
  const last = TORSO[TORSO.length - 1];
  if (!first || !last) throw new Error('otto: empty torso table');
  if (z <= first.z) return first;
  if (z >= last.z) return last;
  for (let i = 0; i < TORSO.length - 1; i++) {
    const a = TORSO[i];
    const b = TORSO[i + 1];
    if (!a || !b || z < a.z || z > b.z) continue;
    const t = smoothstep(a.z, b.z, z);
    return {
      top: THREE.MathUtils.lerp(a.top, b.top, t),
      bottom: THREE.MathUtils.lerp(a.bottom, b.bottom, t),
      halfWidth: THREE.MathUtils.lerp(a.halfWidth, b.halfWidth, t),
    };
  }
  return last;
}

const TORSO_BACK = TORSO[0]?.z ?? 0;
const TORSO_FRONT = TORSO[TORSO.length - 1]?.z ?? 0;

/**
 * The torso as a function of two parameters — the single most useful thing in
 * this file.
 *
 * The loft is built from it, and so are the bald patches and the repair seams,
 * which is why a repair can never float off him or sink into him. The previous
 * pass generated patches as their own ellipsoids positioned by hand near where
 * the body was believed to be; they sat on the surface at one angle and hovered
 * at every other one.
 *
 * `u` is 0 at the rump and 1 at the neck opening. `theta` is 0 at the spine and
 * runs round through the flank to the belly.
 */
function torsoPoint(u: number, theta: number, offset = 0): THREE.Vector3 {
  const z = THREE.MathUtils.lerp(TORSO_BACK, TORSO_FRONT, u);
  const section = torsoSection(z);
  const centreY = (section.top + section.bottom) / 2;
  const halfHeight = (section.top - section.bottom) / 2;

  // The rump closes into a ball instead of a flat cap. Confined to the rear 12 %
  // so it cannot eat into the barrel — a cap that reaches forward is how the
  // first attempt lost its hindquarter mass entirely.
  const cap = u < 0.12 ? Math.sqrt(Math.max(0, 1 - ((0.12 - u) / 0.12) ** 2)) : 1;

  // Sections go from round at the rump to slab-sided at the shoulder. A shoulder
  // shield is flat-flanked and square-shouldered; an ellipse there reads as a
  // barrel however wide it is made, and widening a barrel to compensate is how
  // attempt two got fat instead of powerful.
  const power = THREE.MathUtils.lerp(1, 0.78, smoothstep(-0.6, 0.28, z));
  const sx = Math.sin(theta);
  const cz = Math.cos(theta);
  const shaped = (v: number): number => Math.sign(v) * Math.abs(v) ** power;

  const width = section.halfWidth * cap + offset;
  const height = halfHeight * cap + offset;
  // The crest rides the top of the section only. The eighth power keeps it about
  // three radial samples wide — a ridge of pile, not a raised back.
  const crest = crestAt(z) * Math.max(0, cz) ** 5;

  return new THREE.Vector3(width * shaped(sx), centreY + height * shaped(cz) + crest, z);
}

/** Outward normal of `torsoPoint`, by finite difference. Used to lift repairs off him. */
function torsoNormal(u: number, theta: number): THREE.Vector3 {
  const h = 0.004;
  const du = torsoPoint(Math.min(1, u + h), theta).sub(torsoPoint(Math.max(0, u - h), theta));
  const dt = torsoPoint(u, theta + h).sub(torsoPoint(u, theta - h));
  const n = dt.cross(du).normalize();
  // Degenerate at the rump cap, where du collapses. Falling back to the radial
  // direction is right there and harmless everywhere else.
  if (!Number.isFinite(n.x) || n.lengthSq() < 0.5) {
    return new THREE.Vector3(Math.sin(theta), Math.cos(theta), 0).normalize();
  }
  return n;
}

/**
 * A parametric grid, closed in theta, with a real uv.
 *
 * The uv is not decoration. D-073: hand-built geometry has no uv, `mergeGeometries`
 * demands one, and the failure lands on the *room*, which then refuses to boot.
 * Two assets by two authors hit it in one session. Every hand-built surface in
 * this file carries one.
 */
function lattice(
  rings: number,
  radial: number,
  point: (u: number, theta: number) => THREE.Vector3,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i <= rings; i++) {
    const u = i / rings;
    for (let j = 0; j <= radial; j++) {
      const v = j / radial;
      const p = point(u, v * Math.PI * 2);
      positions.push(p.x, p.y, p.z);
      uvs.push(v, u);
    }
  }
  for (let i = 0; i < rings; i++) {
    for (let j = 0; j < radial; j++) {
      const a = i * (radial + 1) + j;
      const b = a + radial + 1;
      indices.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/** Close one end of a lattice with a fan to a centre vertex. */
function fanCap(geo: THREE.BufferGeometry, radial: number, ring: number, centre: THREE.Vector3): void {
  const pos = geo.getAttribute('position');
  const uv = geo.getAttribute('uv');
  const index = geo.getIndex();
  if (!index) return;
  const positions = Array.from(pos.array);
  const uvs = Array.from(uv.array);
  const indices = Array.from(index.array);
  const centreIndex = positions.length / 3;
  positions.push(centre.x, centre.y, centre.z);
  uvs.push(0.5, ring === 0 ? 0 : 1);
  const base = ring * (radial + 1);
  for (let j = 0; j < radial; j++) {
    if (ring === 0) indices.push(centreIndex, base + j, base + j + 1);
    else indices.push(centreIndex, base + j + 1, base + j);
  }
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
}

function buildTorsoGeometry(): THREE.BufferGeometry {
  const geo = lattice(SEG.torsoRings, SEG.torsoRadial, (u, theta) => torsoPoint(u, theta));
  // Only the neck opening is capped. It is buried inside the skull at rest, but
  // the head turns in stage 4, and an open tube would show a hole the moment it
  // does. The rump needs no cap: `torsoPoint` already closes it to a point.
  const front = torsoSection(TORSO_FRONT);
  fanCap(geo, SEG.torsoRadial, SEG.torsoRings, new THREE.Vector3(0, (front.top + front.bottom) / 2, TORSO_FRONT));
  return geo;
}

/** The skull, lofted along its own descending centre-line. */
function buildSkullGeometry(): THREE.BufferGeometry {
  const sample = (t: number): { z: number; cy: number; halfHeight: number; halfWidth: number } => {
    const first = SKULL[0];
    const last = SKULL[SKULL.length - 1];
    if (!first || !last) throw new Error('otto: empty skull table');
    const scaled = t * (SKULL.length - 1);
    const i = Math.min(SKULL.length - 2, Math.floor(scaled));
    const a = SKULL[i] ?? first;
    const b = SKULL[i + 1] ?? last;
    const k = smoothstep(0, 1, scaled - i);
    return {
      z: THREE.MathUtils.lerp(a.z, b.z, k),
      cy: THREE.MathUtils.lerp(a.cy, b.cy, k),
      halfHeight: THREE.MathUtils.lerp(a.halfHeight, b.halfHeight, k),
      halfWidth: THREE.MathUtils.lerp(a.halfWidth, b.halfWidth, k),
    };
  };

  const geo = lattice(SEG.skullRings, SEG.skullRadial, (u, theta) => {
    const s = sample(u);
    // Rounded off at the back so the skull closes inside the shoulder, and at
    // the front so the muzzle meets its disc without a rim.
    const cap = u < 0.1 ? Math.sqrt(Math.max(0, 1 - ((0.1 - u) / 0.1) ** 2)) : 1;
    const c = Math.cos(theta);
    // The jaw is heavier than the brow. A skull that is symmetric top to bottom
    // is a fish; the hanging jowl is what puts weight under an old boar's head.
    const jaw = c < 0 ? 1.1 : 0.94;
    return new THREE.Vector3(
      s.halfWidth * cap * Math.sin(theta),
      s.cy + s.halfHeight * cap * c * jaw,
      s.z,
    );
  });
  const nose = sample(1);
  fanCap(geo, SEG.skullRadial, SEG.skullRings, new THREE.Vector3(0, nose.cy, nose.z));
  return geo;
}

/**
 * A tusk: a tapered tube swept along the quadratic through root, control and tip.
 *
 * Eight sides and eight rings. It is 0.31 m of pale felt against dark fabric and
 * it is the only hard shape on him, so it is worth resolving properly — the
 * silhouette gate is decided here more than anywhere else in the file.
 */
function buildTusk(side: 1 | -1, origin: THREE.Vector3): THREE.BufferGeometry {
  const at = (t: number): THREE.Vector3 => {
    const k = 1 - t;
    return new THREE.Vector3(
      side * (k * k * TUSK.root.x + 2 * k * t * TUSK.control.x + t * t * TUSK.tip.x),
      k * k * TUSK.root.y + 2 * k * t * TUSK.control.y + t * t * TUSK.tip.y,
      k * k * TUSK.root.z + 2 * k * t * TUSK.control.z + t * t * TUSK.tip.z,
    ).sub(origin);
  };

  const geo = lattice(7, 7, (u, theta) => {
    const centre = at(u);
    const ahead = at(Math.min(1, u + 0.05));
    const behind = at(Math.max(0, u - 0.05));
    const tangent = ahead.clone().sub(behind).normalize();
    const right = new THREE.Vector3(0, 0, 1).cross(tangent).normalize();
    const up = tangent.clone().cross(right).normalize();
    const r = THREE.MathUtils.lerp(TUSK.rootRadius, TUSK.tipRadius, u * u);
    return centre
      .add(right.multiplyScalar(Math.cos(theta) * r))
      .add(up.multiplyScalar(Math.sin(theta) * r));
  });
  fanCap(geo, 7, 7, at(1));
  return geo;
}

/**
 * One ear, built from a table so the two can be genuinely different objects.
 *
 * The pivot sits *inside* the skull and the geometry starts below the surface —
 * the first attempt's ears were floating primitives, and the reason was that
 * they began where the skull ended.
 */
function buildEar(
  spec: {
    side: 1 | -1;
    x: number;
    y: number;
    z: number;
    length: number;
    width: number;
    splay: number;
    tilt: number;
    yaw: number;
  },
  origin: THREE.Vector3,
): THREE.BufferGeometry {
  const geo = lattice(7, 9, (u, theta) => {
    // Starts at −0.25 so a quarter of the ear is buried in the skull.
    const t = u * 1.25 - 0.25;
    // Tapers to a tenth of its base, not to a third. At a third the blade is a
    // rectangle, and a rectangle standing on a skull reads as damage rather than
    // as an ear — which is exactly how it looked in the side silhouette.
    const taper = (1 - 0.9 * Math.max(0, t) ** 1.5) * Math.min(1, (t + 0.25) * 3);
    return new THREE.Vector3(
      // Thin across, broad fore-and-aft. **This is the fix for the ear reading
      // as a sliver in the side silhouette**, and it was a real bug rather than
      // a proportion: the blade was built broad in X and thin in Z, so a camera
      // looking down X — which is the turnaround's side view, and the view the
      // beat frames him in — saw 28 mm of edge. A pig's ear is a flap whose
      // plane stands fore-and-aft. Rosalie's ears escape this because hers flop
      // forward until their broad face points sideways; his stand up.
      Math.sin(theta) * spec.width * 0.46 * taper,
      t * spec.length,
      // Cupped forward, so it catches the light as a shell rather than a card.
      Math.cos(theta) * spec.width * taper + 0.34 * spec.width * Math.max(0, t) ** 2,
    );
  });
  fanCap(geo, 9, 7, new THREE.Vector3(0, spec.length, 0.34 * spec.width));
  geo.rotateX(spec.tilt);
  geo.rotateZ(spec.side * -spec.splay);
  // Turned obliquely, so neither the side view nor the front view catches the
  // blade edge-on. Straight fore-and-aft would only move the problem 90°.
  geo.rotateY(spec.side * spec.yaw);
  geo.translate(spec.side * spec.x - origin.x, spec.y - origin.y, spec.z - origin.z);
  return geo;
}

/**
 * One leg as a single swept tube through sampled joints.
 *
 * "Four truncated cones" was failure six of the first attempt, and stacking two
 * cylinders instead — the second attempt — only made it two cones. A leg is one
 * continuous limb whose radius swells at the joints and pinches at the cannon,
 * and the hind pair's z values zigzag because a hock does. That zigzag is what
 * stops a four-legged animal reading as a table.
 */
function buildLeg(joints: readonly { y: number; z: number; r: number }[], x: number): THREE.BufferGeometry {
  const sample = (t: number): { y: number; z: number; r: number } => {
    const first = joints[0];
    const last = joints[joints.length - 1];
    if (!first || !last) throw new Error('otto: empty leg table');
    const scaled = t * (joints.length - 1);
    const i = Math.min(joints.length - 2, Math.floor(scaled));
    const a = joints[i] ?? first;
    const b = joints[i + 1] ?? last;
    const k = smoothstep(0, 1, scaled - i);
    return {
      y: THREE.MathUtils.lerp(a.y, b.y, k),
      z: THREE.MathUtils.lerp(a.z, b.z, k),
      r: THREE.MathUtils.lerp(a.r, b.r, k),
    };
  };
  return lattice(9, SEG.limb, (u, theta) => {
    const s = sample(u);
    return new THREE.Vector3(x + Math.sin(theta) * s.r, s.y, s.z + Math.cos(theta) * s.r * 1.15);
  });
}

/** A trotter: squashed, longer than wide, and set slightly ahead of the cannon. */
function buildHoof(x: number, z: number): THREE.BufferGeometry {
  const hoof = new THREE.SphereGeometry(0.062, 8, 5);
  hoof.scale(1, 0.55, 1.25);
  hoof.translate(x, 0.034, z);
  return hoof;
}

/**
 * The tail. Hanging, kinked, and emphatically not curled.
 *
 * Rosalie's curl is a piglet's, and it is her cheapest species signal. Otto lost
 * his a long time ago: his hangs down the back of a wasted rump with one kink in
 * it, which is both the older animal and a different shape from hers. The first
 * attempt's straight vertical spike was a silhouette failure from behind and the
 * brief had forbidden it in as many words.
 */
function buildTail(): THREE.BufferGeometry {
  const joints = [
    { y: 0.65, z: -1.1, r: 0.03 },
    { y: 0.6, z: -1.145, r: 0.024 },
    { y: 0.53, z: -1.155, r: 0.016 },
    { y: 0.475, z: -1.12, r: 0.013 },
    { y: 0.44, z: -1.125, r: 0.007 },
  ] as const;
  return buildLeg(joints, 0);
}

/**
 * A repair, generated on the torso's own surface and lifted off it by `lift`.
 *
 * This is the fix for failures seven and eight. Bald patches were "dark blobs
 * sitting on the surface" and seams were "single straight lines, reading as
 * scratches", and both had the same cause: they were separate primitives placed
 * near where the body was believed to be. Sampling `torsoPoint` means a patch is
 * a piece of him by construction, at every angle, forever.
 */
function surfacePatch(
  u0: number,
  u1: number,
  theta0: number,
  theta1: number,
  lift: number,
  wobble: number,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const rings = 5;
  const cols = 8;
  for (let i = 0; i <= rings; i++) {
    const a = i / rings;
    for (let j = 0; j <= cols; j++) {
      const b = j / cols;
      // Polar inside the patch, so the outline is a closed lobed blob. Worn
      // fabric has a ragged edge; a clean ellipse reads as paint however proud
      // of the surface it stands.
      const angle = b * Math.PI * 2;
      const ragged = 1 + wobble * Math.sin(angle * 3 + u0 * 40) + wobble * 0.6 * Math.sin(angle * 5 - u0 * 25);
      const du = ((u1 - u0) / 2) * a * ragged * Math.cos(angle);
      const dt = ((theta1 - theta0) / 2) * a * ragged * Math.sin(angle);
      const u = (u0 + u1) / 2 + du;
      const theta = (theta0 + theta1) / 2 + dt;
      // The centre stands proudest and the rim meets the body, so the edge is a
      // fold rather than a step.
      const p = torsoPoint(u, theta).add(torsoNormal(u, theta).multiplyScalar(lift * (1 - a * a * 0.85)));
      positions.push(p.x, p.y, p.z);
      uvs.push(b, a);
    }
  }
  for (let i = 0; i < rings; i++) {
    for (let j = 0; j < cols; j++) {
      const a = i * (cols + 1) + j;
      const b = a + cols + 1;
      indices.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/**
 * A mended split: the closed seam itself, so the stitches have something to hold.
 *
 * A row of crosses with nothing under it is decoration. The dark line is the cut
 * that was sewn up, and it is what turns the crosses into a repair.
 */
function buildSeamLine(u0: number, u1: number, theta: number): THREE.BufferGeometry {
  return lattice(8, 5, (u, phi) => {
    const t = THREE.MathUtils.lerp(u0, u1, u);
    const centre = torsoPoint(t, theta).add(torsoNormal(t, theta).multiplyScalar(0.004));
    const along = torsoPoint(Math.min(1, t + 0.01), theta).sub(torsoPoint(Math.max(0, t - 0.01), theta)).normalize();
    const out = torsoNormal(t, theta);
    const side = along.clone().cross(out).normalize();
    const r = 0.008;
    return centre
      .add(side.multiplyScalar(Math.cos(phi) * r))
      .add(out.multiplyScalar(Math.sin(phi) * r * 0.5));
  });
}

/** Cross-stitches straddling a seam, sitting on the body rather than near it. */
function buildStitchRow(u0: number, u1: number, theta: number, count: number): THREE.BufferGeometry[] {
  const pieces: THREE.BufferGeometry[] = [];
  for (let i = 0; i < count; i++) {
    const t = THREE.MathUtils.lerp(u0, u1, (i + 0.5) / count);
    const centre = torsoPoint(t, theta).add(torsoNormal(t, theta).multiplyScalar(0.007));
    const along = torsoPoint(Math.min(1, t + 0.01), theta).sub(torsoPoint(Math.max(0, t - 0.01), theta)).normalize();
    const out = torsoNormal(t, theta);
    const side = along.clone().cross(out).normalize();
    for (const lean of [1, -1] as const) {
      // Four sides is enough thread at 16 mm; six buys no silhouette and this
      // detail must never reach the outline in the first place.
      const stitch = new THREE.CylinderGeometry(0.0075, 0.0075, 0.062, 4, 1);
      const axis = along.clone().multiplyScalar(lean).add(side.clone().multiplyScalar(1.15)).normalize();
      stitch.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), axis));
      stitch.translate(centre.x, centre.y, centre.z);
      pieces.push(stitch);
    }
  }
  return pieces;
}

function mesh(
  geometry: THREE.BufferGeometry,
  material: THREE.Material | THREE.Material[],
  name: string,
): THREE.Mesh {
  const result = new THREE.Mesh(geometry, material);
  result.name = name;
  result.castShadow = true;
  result.receiveShadow = true;
  return result;
}

function pivot(name: string, x: number, y: number, z: number): THREE.Object3D {
  const result = new THREE.Object3D();
  result.name = name;
  result.position.set(x, y, z);
  return result;
}

function merged(name: string, pieces: readonly THREE.BufferGeometry[]): THREE.BufferGeometry {
  const geometry = mergeGeometries([...pieces], false);
  if (!geometry) throw new Error(`otto: could not merge ${name}`);
  return geometry;
}

/** Merge material islands into one mesh without losing their deliberately different dyes. */
function mergedMesh(
  name: string,
  pieces: readonly { geometry: THREE.BufferGeometry; material: THREE.Material }[],
): THREE.Mesh {
  const geometry = mergeGeometries(
    pieces.map((piece) => piece.geometry),
    true,
  );
  if (!geometry) throw new Error(`otto: could not merge ${name}`);
  return mesh(
    geometry,
    pieces.map((piece) => piece.material),
    name,
  );
}

// Front pair set under the shoulder and back pair well forward of the rump: his
// hindquarters overhang his hocks, which is what a heavy front end does to an
// old animal's stance and reads instantly as age.
const FRONT_LEG = [
  { y: 0.44, z: 0.215, r: 0.118 },
  { y: 0.33, z: 0.205, r: 0.108 },
  { y: 0.24, z: 0.192, r: 0.092 },
  { y: 0.16, z: 0.186, r: 0.072 },
  { y: 0.085, z: 0.19, r: 0.054 },
  { y: 0.048, z: 0.202, r: 0.058 },
] as const;

const HIND_LEG = [
  { y: 0.46, z: -0.83, r: 0.108 },
  { y: 0.36, z: -0.858, r: 0.102 },
  { y: 0.28, z: -0.892, r: 0.084 },
  { y: 0.2, z: -0.872, r: 0.062 },
  { y: 0.105, z: -0.845, r: 0.05 },
  { y: 0.048, z: -0.838, r: 0.055 },
] as const;

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'otto';

  // `roll` sits at his centre of mass and `breath` cancels it back to the floor,
  // so a Y-only scale on `breath` lifts his mass without pulling his hooves
  // through the concrete. `settle` is construction correction and nothing else —
  // keeping it off the two animated nodes means a later proportion change cannot
  // silently move the axis stage 4 rolls him about.
  const roll = pivot('roll', 0, ROLL_Y, 0);
  const breath = pivot('breath', 0, -ROLL_Y, 0);
  const settle = pivot('settle', 0, 0, 0);
  group.add(roll);
  roll.add(breath);
  breath.add(settle);

  const fabric = surface('fabric', OTTO.fabric);
  const shadow = surface('fabric', OTTO.fabricShadow);
  const bald = surface('fabric', OTTO.bald);
  const thread = surface('fabric', OTTO.thread);
  const patchFabric = surface('fabric', OTTO.patch);
  const button = surface('fabric', OTTO.button);
  const tuskFabric = surface('fabric', OTTO.tusk);

  settle.add(mesh(buildTorsoGeometry(), fabric, 'body'));

  // ── head ───────────────────────────────────────────────────────────────────
  const head = pivot('head', 0, NECK.y, NECK.z);
  settle.add(head);
  const origin = new THREE.Vector3(0, NECK.y, NECK.z);

  head.add(mesh(buildSkullGeometry(), fabric, 'head-mesh'));

  // The snout disc, and it is the family resemblance: Rosalie has the same flat
  // face on the same species of toy. Rounded rim rather than a cylinder cap —
  // a hard edge gave her a boxy profile at 64 px and would do the same here.
  const noseSample = SKULL[SKULL.length - 1];
  const noseY = noseSample?.cy ?? 0;
  const disc = new THREE.SphereGeometry(0.094, 10, 7);
  disc.scale(1, 0.86, 0.32);
  disc.translate(0, noseY - 0.008, DISC_Z);
  head.add(mesh(disc, shadow, 'snout-disc'));

  const nostrils: THREE.BufferGeometry[] = [];
  for (const side of [1, -1] as const) {
    const nostril = new THREE.SphereGeometry(0.019, 6, 5);
    nostril.scale(0.8, 1.15, 0.7);
    // Standing proud of the disc, not sunk into it. Sunk, they have no edge to
    // catch light and vanish — which is exactly what happened to Rosalie's until
    // they were pushed out.
    nostril.translate(side * 0.034, noseY - 0.016, DISC_Z + 0.014);
    nostrils.push(nostril);
  }
  head.add(mesh(merged('nostrils', nostrils), button, 'nostrils'));

  // Eyes: small, high, and set back under the brow. Forward eyes turn a heavy old
  // boar into a cute toy face, which is the opposite of what this beat needs —
  // he has to be *enormous* first and glad to see her second.
  const eyes: THREE.BufferGeometry[] = [];
  for (const side of [1, -1] as const) {
    const eye = new THREE.SphereGeometry(0.028, 8, 6);
    eye.scale(1, 0.85, 0.55);
    eye.translate(side * 0.14, 0.01, 0.18);
    eyes.push(eye);
  }
  head.add(mesh(merged('eyes', eyes), button, 'eyes'));

  // His ear, and the one somebody sewed on out of another toy. Different shape,
  // different size, different angle, different fabric — an asymmetric head is a
  // far stronger 64 px silhouette than a balanced one, and here it is also the
  // sentence "he has been kept, and mended, for a very long time".
  head.add(
    mergedMesh('ears', [
      {
        geometry: buildEar(
          { side: 1, x: 0.148, y: 0.795, z: 0.635, length: 0.255, width: 0.098, splay: 0.46, tilt: -0.26, yaw: 0.42 },
          origin,
        ),
        material: fabric,
      },
      {
        geometry: buildEar(
          { side: -1, x: 0.142, y: 0.785, z: 0.62, length: 0.2, width: 0.108, splay: 0.82, tilt: 0.14, yaw: 0.26 },
          origin,
        ),
        material: patchFabric,
      },
    ]),
  );

  head.add(mesh(merged('tusks', [buildTusk(1, origin), buildTusk(-1, origin)]), tuskFabric, 'tusks'));

  // ── legs ───────────────────────────────────────────────────────────────────
  settle.add(
    mergedMesh('legs', [
      {
        geometry: merged('leg shafts', [
          buildLeg(FRONT_LEG, 0.235),
          buildLeg(FRONT_LEG, -0.235),
          buildLeg(HIND_LEG, 0.185),
          buildLeg(HIND_LEG, -0.185),
        ]),
        material: fabric,
      },
      {
        geometry: merged('hooves', [
          buildHoof(0.235, 0.208),
          buildHoof(-0.235, 0.202),
          buildHoof(0.185, -0.832),
          buildHoof(-0.185, -0.842),
        ]),
        material: shadow,
      },
    ]),
  );

  settle.add(mesh(buildTail(), fabric, 'tail'));

  // ── the repairs ────────────────────────────────────────────────────────────
  // Worn through on the shoulder he leans on and the hip he lies on: the two
  // places a real animal in a concrete pen loses its coat. Both are generated on
  // the torso surface, so neither can float.
  settle.add(
    mesh(
      merged('bald patches', [
        surfacePatch(0.79, 0.93, 1.05, 1.62, 0.004, 0.13),
        surfacePatch(0.2, 0.34, -1.5, -0.95, 0.004, 0.16),
      ]),
      bald,
      'bald-patches',
    ),
  );

  const seams: THREE.BufferGeometry[] = [
    buildSeamLine(0.58, 0.76, 1.28),
    buildSeamLine(0.3, 0.44, -1.9),
  ];
  settle.add(mesh(merged('seams', seams), shadow, 'repair-seams'));
  settle.add(
    mesh(
      merged('stitches', [
        ...buildStitchRow(0.58, 0.76, 1.28, 5),
        ...buildStitchRow(0.3, 0.44, -1.9, 4),
      ]),
      thread,
      'repair-stitches',
    ),
  );

  // ── the contract, enforced ─────────────────────────────────────────────────
  // Measured, not asserted from the numbers above. D-074: `footprint` is what the
  // mesh measures, and the way that decision got made was three assets shipping
  // with a footprint that described the author's intent instead of the geometry.
  group.updateMatrixWorld(true);
  const raw = new THREE.Box3().setFromObject(group);
  settle.position.set(-(raw.min.x + raw.max.x) / 2, -raw.min.y, -(raw.min.z + raw.max.z) / 2);

  group.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(group);
  const size = bounds.getSize(new THREE.Vector3());
  const drift: string[] = [];
  if (Math.abs(bounds.min.y) > 0.001) drift.push(`sits ${bounds.min.y.toFixed(4)} m off the ground`);
  if (Math.abs(size.x - OTTO_SCALE.width) > SCALE_TOLERANCE) drift.push(`width ${size.x.toFixed(3)}`);
  if (Math.abs(size.y - OTTO_SCALE.height) > SCALE_TOLERANCE) drift.push(`height ${size.y.toFixed(3)}`);
  if (Math.abs(size.z - OTTO_SCALE.length) > SCALE_TOLERANCE) drift.push(`length ${size.z.toFixed(3)}`);
  if (drift.length > 0) {
    throw new Error(
      `otto: geometry has drifted from OTTO_SCALE (${drift.join(', ')}). ` +
        'Fix the proportions or update OTTO_SCALE — the hall places him against these numbers.',
    );
  }

  return group;
}

/** The only rig nodes animation may address directly. */
export interface OttoParts {
  /** Roll about the long axis, from his centre of mass. Beat 3 stage 4. */
  roll: THREE.Object3D;
  /** Scale in Y only; its origin is the floor, so ground contact holds. */
  breath: THREE.Object3D;
  /** Neck pivot, buried in the shoulder rather than perched on it. */
  head: THREE.Object3D;
}

function required(root: THREE.Object3D, name: string): THREE.Object3D {
  const found = root.getObjectByName(name);
  if (!found) throw new Error(`otto: rig node "${name}" missing — was the mesh renamed?`);
  return found;
}

export function findOttoParts(root: THREE.Object3D): OttoParts {
  return { roll: required(root, 'roll'), breath: required(root, 'breath'), head: required(root, 'head') };
}

export const ottoAsset: AssetDefinition = {
  id: 'otto',
  label: 'Otto',
  description:
    'An enormous patched old boar. The topline rises to a crest over the shoulder and the head hangs below it — two peaks and a valley, which is the whole silhouette. Felt tusks cross the outline; one ear came off another toy. Too large to follow Rosalie through the slat or the vent.',
  footprint: { width: OTTO_SCALE.width, height: OTTO_SCALE.height, length: OTTO_SCALE.length },
  create: build,
};
